import React, { useEffect, useState } from 'react'
import axios from 'axios'
import useSWR from 'swr'
import { basicFetcher } from '../../utils/api/swr-fetcher'
import {
  Alert, type AlertColor, Button, FormControl, FormHelperText, InputLabel, MenuItem, Select, Snackbar, Stack,
  TextField, Typography, Card, CardContent, Grid, useTheme, useMediaQuery,
} from '@mui/material'
import EastIcon from '@mui/icons-material/East'
import SouthIcon from '@mui/icons-material/South'
import { type IWithdrawAccount } from '../../models/withdraw/withdraw-info-types'
import { isEmptyString, isValidNumberString } from '../../utils/string-utils'
import CenteredCardLayout from '../../layouts/centered-card-layout'

const WithdrawView: React.FC = () => {
  const theme = useTheme()
  const isLargerThanMD = useMediaQuery(theme.breakpoints.up('md'))

  const { data, error } = useSWR<IWithdrawAccount[], boolean>(
    process.env.REACT_APP_API_WITHDRAW_INDEX,
    basicFetcher,
    {
      shouldRetryOnError: false,
    },
  )

  const [withdrawDetails, setWithdrawDetails] = useState<Map<string, Map<string, IWithdrawAccount>>>(new Map())

  useEffect(() => {
    let dataToProcess: IWithdrawAccount[]
    if (error === true || data === undefined) {
      dataToProcess = []
    } else {
      dataToProcess = data
    }

    const withdrawDetails = new Map<string, Map<string, IWithdrawAccount>>()

    for (const item of dataToProcess) {
      const accountName = String(item.account_name)
      if (!withdrawDetails.has(accountName)) {
        withdrawDetails.set(accountName, new Map<string, IWithdrawAccount>())
      }
      const accountMap = withdrawDetails.get(accountName)
      if (accountMap != null) {
        const coin = String(item.coin)
        accountMap.set(coin, item)
      }
    }

    setWithdrawDetails(withdrawDetails)
  }, [data, error])

  // Source
  const [sourceAccount, setSourceAccount] = useState<string>('')
  const [sourceCoin, setSourceCoin] = useState<string>('')
  const [sourceAmount, setSourceAmount] = useState<string>('')
  // Target
  const [targetAccount, setTargetAccount] = useState<string>('')
  const [targetCoin, setTargetCoin] = useState<string>('')

  const [toastOpen, setToastOpen] = useState(false)
  const [toastMessage, setToastMessage] = useState('')
  const [toastSeverity, setToastSeverity] = useState<AlertColor>('info')
  const displayToastWithMessage = (message: string, severity: AlertColor): void => {
    setToastMessage(message)
    setToastSeverity(severity)
    setToastOpen(true)
  }

  const onSubmit = (event: React.FormEvent): void => {
    event.preventDefault()

    if (process.env.REACT_APP_API_WITHDRAW_EXECUTE == null) {
      console.error('[Withdraw] Failed to execute withdraw (reason: invalid execute url)')
      return
    }

    const sourceDetails = withdrawDetails.get(sourceAccount)?.get(sourceCoin)
    const targetDetails = withdrawDetails.get(targetAccount)?.get(targetCoin)
    console.debug('[Withdraw] Submitting withdraw:')
    const body = {
      source: {
        account_name: sourceAccount,
        exchange: sourceDetails?.exchange,
        fees: sourceDetails?.fees ?? '0',
      },
      target: {
        account_name: targetAccount,
        exchange: targetDetails?.exchange,
        fees: targetDetails?.fees ?? '0',
      },
      coin: sourceCoin,
      exchange_network: sourceDetails?.exchange_network,
      address: targetDetails?.address,
      amount: sourceAmount,
    }
    console.debug(body)

    axios
      .post(process.env.REACT_APP_API_WITHDRAW_EXECUTE, body)
      .then(response => {
        console.debug(response)
        if (response != null) {
          const withdrawId = response?.data?.withdraw_id as string

          displayToastWithMessage(
            `Withdraw Succeeded
            Withdraw Id: ${withdrawId}
            `,
            'success',
          )
        }
      })
      .catch(reason => {
        console.error(reason)
        if (reason != null) {
          const message = reason?.message as string
          const error = reason?.response?.data?.error as string
          const errorExecution = reason?.response?.data?.error_execution as string

          displayToastWithMessage(
            `Withdraw Failed
          Message: ${message}
          Error: ${error}
          Error_Execution: ${errorExecution}
          ( Full error can also be viewed in the console logs )`,
            'error',
          )
        }
      })
  }

  // eslint-disable-next-line @typescript-eslint/no-misused-promises
  return (
    <CenteredCardLayout addPadding>
      <form id='withdraw-form' onSubmit={onSubmit}>
        <Stack gap={2} alignItems='center'>
          <Card variant='outlined' sx={{ width: 1, padding: 2 }}>
            <CardContent>
              <Grid container alignItems='center' spacing={4}>
                <Grid
                  item xs
                  display='flex'
                  flexDirection='column'
                  gap={2}
                >
                  <FormControl
                    variant='standard'
                    sx={{ minWidth: 300, maxWidth: { xs: 'none', md: 300 }, minHeight: '4.5rem' }}
                    error={(!isEmptyString(sourceAccount) && !isEmptyString(targetAccount) && sourceAccount === targetAccount)}
                  >
                    <InputLabel id='source-account-select-label'>Source Account</InputLabel>
                    <Select
                      labelId='source-account-select-label'
                      id='source-account-select'
                      label='Source Account'
                      value={sourceAccount}
                      onChange={event => {
                        setSourceAccount(event.target.value)
                      }}
                    >
                      {
                        Array.from(withdrawDetails.keys())
                          .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
                          .map(key => {
                            return <MenuItem key={key} value={key}>{key}</MenuItem>
                          })
                      }
                    </Select>
                    {
                      (!isEmptyString(sourceAccount) && !isEmptyString(targetAccount) && sourceAccount === targetAccount) &&
                      <FormHelperText>Source and target account must be different</FormHelperText>
                    }
                  </FormControl>

                  <FormControl
                    variant='standard'
                    sx={{ minWidth: 300, maxWidth: { xs: 'none', md: 300 }, minHeight: '4.5rem' }}
                    disabled={isEmptyString(sourceAccount)}
                    error={(!isEmptyString(sourceCoin) && sourceCoin !== targetCoin && !isEmptyString(targetCoin))}
                  >
                    <InputLabel id='source-coin-select-label'>Source Coin</InputLabel>
                    <Select
                      labelId='source-coin-select-label'
                      id='source-coin-select'
                      label='Source Coin'
                      value={sourceCoin}
                      onChange={event => {
                        setSourceCoin(event.target.value)
                      }}
                    >
                      {
                        sourceAccount == null || sourceAccount.length === 0
                          ? null
                          : Array.from(withdrawDetails.get(sourceAccount)?.keys() ?? [])
                            .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
                            .map(key => {
                              return <MenuItem key={key} value={key}>{key}</MenuItem>
                            })
                      }
                    </Select>
                    {
                      (!isEmptyString(sourceCoin) && sourceCoin !== targetCoin && !isEmptyString(targetCoin)) &&
                      <FormHelperText>Source and target coin must match</FormHelperText>
                    }
                  </FormControl>

                  <Stack>
                    <Typography>
                      Network: {withdrawDetails.get(sourceAccount)?.get(sourceCoin)?.network ?? '-'}
                    </Typography>
                    <Typography>
                      Fees: {withdrawDetails.get(sourceAccount)?.get(sourceCoin)?.fees ?? '-'}
                    </Typography>
                  </Stack>

                  <Stack>
                    <Typography>
                      Exchange: {withdrawDetails.get(sourceAccount)?.get(sourceCoin)?.exchange ?? '-'}
                    </Typography>
                    <Typography variant='caption'>
                      Exchange Network: {withdrawDetails.get(sourceAccount)?.get(sourceCoin)?.exchange_network ?? '-'}
                    </Typography>
                  </Stack>
                </Grid>

                <Grid
                  item xs={12} md='auto'
                  display='flex'
                  flexDirection='column'
                  alignItems='center'
                  minWidth='60px'
                  gap={2}
                >
                  {
                    isLargerThanMD ? <EastIcon fontSize='large'/> : <SouthIcon fontSize='large'/>
                  }
                </Grid>

                <Grid
                  item xs
                  display='flex'
                  flexDirection='column'
                  gap={2}
                >
                  <FormControl
                    variant='standard'
                    sx={{ minWidth: 300, maxWidth: { xs: 'none', md: 300 }, minHeight: '4.5rem' }}
                    error={(!isEmptyString(sourceAccount) && !isEmptyString(targetAccount) && sourceAccount === targetAccount)}
                  >
                    <InputLabel id='target-account-select-label'>Target Account</InputLabel>
                    <Select
                      labelId='target-account-select-label'
                      id='target-account-select'
                      label='Target Account'
                      value={targetAccount}
                      onChange={event => {
                        setTargetAccount(event.target.value)
                      }}
                    >
                      {
                        Array.from(withdrawDetails.keys())
                          .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
                          .map(key => {
                            return <MenuItem key={key} value={key}>{key}</MenuItem>
                          })
                      }
                    </Select>
                    {
                      (!isEmptyString(sourceAccount) && !isEmptyString(targetAccount) && sourceAccount === targetAccount) &&
                      <FormHelperText>Source and target account must be different</FormHelperText>
                    }
                  </FormControl>

                  <FormControl
                    variant='standard'
                    sx={{ minWidth: 300, maxWidth: { xs: 'none', md: 300 }, minHeight: '4.5rem' }}
                    disabled={isEmptyString(targetAccount)}
                    error={(!isEmptyString(sourceCoin) && sourceCoin !== targetCoin && !isEmptyString(targetCoin))}
                  >
                    <InputLabel id='target-coin-select-label'>Target Coin</InputLabel>
                    <Select
                      labelId='target-coin-select-label'
                      id='target-coin-select'
                      label='Target Coin'
                      value={targetCoin}
                      onChange={event => {
                        setTargetCoin(event.target.value)
                      }}
                    >
                      {
                        targetAccount == null || targetAccount.length === 0
                          ? null
                          : Array.from(withdrawDetails.get(targetAccount)?.keys() ?? [])
                            .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
                            .map(key => {
                              return <MenuItem key={key} value={key}>{key}</MenuItem>
                            })
                      }
                    </Select>
                    {
                      (!isEmptyString(sourceCoin) && sourceCoin !== targetCoin && !isEmptyString(targetCoin)) &&
                      <FormHelperText>Source and target coin must match</FormHelperText>
                    }
                  </FormControl>

                  <Stack>
                    <Typography>
                      Network: {withdrawDetails.get(targetAccount)?.get(targetCoin)?.network ?? '-'}
                    </Typography>
                    <Typography>
                      Fees: {withdrawDetails.get(targetAccount)?.get(targetCoin)?.fees ?? '-'}
                    </Typography>
                  </Stack>

                  <Stack>
                    <Typography>
                      Exchange: {withdrawDetails.get(targetAccount)?.get(targetCoin)?.exchange ?? '-'}
                    </Typography>
                    <Typography variant='caption'>
                      Exchange Network: {withdrawDetails.get(targetAccount)?.get(targetCoin)?.exchange_network ?? '-'}
                    </Typography>
                  </Stack>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Card variant='outlined' sx={{ width: 1, padding: 2 }}>
            <CardContent>
              <Stack gap={4}>
                <TextField
                  id='amount-text-field'
                  label='Amount'
                  type='number'
                  variant='standard'
                  sx={{ minHeight: '4.5rem' }}
                  error={(!(isValidNumberString(sourceAmount) && Number(sourceAmount) > 0))}
                  helperText={
                    (!(isValidNumberString(sourceAmount) && Number(sourceAmount) > 0)) &&
                    'Amount must be a positive number greater than zero'
                  }
                  value={sourceAmount}
                  onChange={event => {
                    setSourceAmount(event.target.value)
                  }}
                />

                <Button
                  variant='contained'
                  type='submit'
                  disabled={
                    (isEmptyString(sourceCoin) || isEmptyString(targetCoin)) ||
                    (sourceCoin !== targetCoin) ||
                    (!(isValidNumberString(sourceAmount) && Number(sourceAmount) > 0)) ||
                    (isEmptyString(sourceAccount) || isEmptyString(targetAccount))
                  }
                >
                  Submit
                </Button>

                <Snackbar
                  anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                  autoHideDuration={60000}
                  open={toastOpen}
                  onClose={() => {
                    setToastOpen(false)
                  }}
                >
                  <Alert
                    sx={{ width: 1, fontSize: 'medium', whiteSpace: 'pre-line' }}
                    variant='filled'
                    onClose={() => {
                      setToastOpen(false)
                    }}
                    severity={toastSeverity}
                  >
                    {toastMessage}
                  </Alert>
                </Snackbar>
              </Stack>
            </CardContent>
          </Card>
        </Stack>
      </form>
    </CenteredCardLayout>
  )
}

export default WithdrawView
